import { useId } from "react";
import { useTranslation } from "react-i18next";

import {
  describeOrderLineHookVideos,
  orderLineHookLabel,
} from "../../lib/order-line-hooks";
import type { OrderScriptRow } from "../../types/marketplace.types";
import { ScriptReviewAction } from "../ScriptReviewAction";

export function OrderLineHookVideos({
  script,
  title,
}: {
  script: OrderScriptRow;
  title: string;
}) {
  const { t } = useTranslation();
  const headingId = useId();
  const { state, videos } = describeOrderLineHookVideos(script);

  if (state === "hidden") return null;

  return (
    <section aria-labelledby={headingId} className="flex flex-col gap-2">
      <h3
        id={headingId}
        className="text-caption tracking-label font-medium uppercase text-muted-foreground"
      >
        {t("marketplace.orders.list.hookVideos.title", "Hook videos")}
      </h3>
      {state === "in-production" ? (
        <p className="text-drawer-body text-muted-foreground">
          {t(
            "marketplace.orders.list.hookVideos.inProduction",
            "Production in progress",
          )}
        </p>
      ) : (
        <ul
          aria-labelledby={headingId}
          className="divide-y divide-border-subtle rounded-md border border-border-subtle"
        >
          {videos.map((video) => {
            const label = orderLineHookLabel(t, video);
            return (
              <li
                key={video.hookKey ?? "primary"}
                className="flex min-h-11 items-center justify-between gap-3 px-3 py-2"
              >
                <span className="text-drawer-body text-foreground">{label}</span>
                {video.scriptReviewId || video.preflightUrl ? (
                  <ScriptReviewAction
                    scriptReviewId={video.scriptReviewId}
                    preflightUrl={video.preflightUrl}
                    label={t("marketplace.orders.list.reviewVideo", "Review video")}
                    ariaLabel={t("marketplace.orders.list.hookVideos.reviewAria", {
                      hook: label,
                      title,
                      defaultValue:
                        "Review the {{hook}} video for {{title}} (opens in a new tab)",
                    })}
                    variant="outline"
                    size="sm"
                    compact
                  />
                ) : (
                  <span className="text-xs text-muted-foreground">
                    {t("marketplace.orders.list.hookVideos.missing", "No video yet")}
                  </span>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}
